import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Class-name combiner — clsx + tailwind-merge (de-dup conflicting tw classes). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** ISO YYYY-MM-DD formatter. */
export function formatDate(d: string | Date): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return String(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Number formatter with fixed decimals + optional unit suffix. */
export function formatNumber(n: number, decimals = 2, unit = ""): string {
  const fixed = n.toFixed(decimals);
  return unit ? `${fixed} ${unit}` : fixed;
}

/** Years between two ISO date strings. */
export function deltaYears(from: string, to: string): number {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  return (b - a) / (365.25 * 24 * 3600 * 1000);
}

/** A translucent wash of `c`, safe when `c` is a CSS custom property.
 *
 *  The hex-alpha shorthand this replaces — `` `${color}14` `` — silently stops
 *  being a colour the moment `color` is `var(--err)`: `"var(--err)14"` is not a
 *  valid value, so the browser discards the whole declaration and the tint
 *  disappears. jsdom does not validate CSS values, so no test can see it.
 *  `color-mix()` composes with variables, so the tint follows the palette.
 *
 *  `pct` is a percentage (8 for the old `14` suffix, 33 for `55`, and so on:
 *  hex alpha `XY` is `parseInt("XY", 16) / 255`). */
export function tint(c: string, pct: number): string {
  return `color-mix(in srgb, ${c} ${pct}%, transparent)`;
}
